import _ from 'lodash';
import { createSlice } from '@reduxjs/toolkit';
import updateEntityVersion from '../../common/actions/updateEntityVersion';
import {
  getCommentsByEntity,
  updateComment,
  resolveCommentThread,
  getCommentCounts,
  getCommentCountsByContentId,
  resolveAllComments,
  addCommentToEntity,
  deleteCommentThread,
} from './commentService';
import { COMMENT_VIEWING_STATUS } from './constants';

const updateValues = (oldComment, newComment) => {
  const copy = { ...oldComment };
  Object.keys(copy).forEach((key) => {
    copy[key] =
      typeof newComment[key] === 'undefined'
        ? oldComment[key]
        : newComment[key];
  });

  return copy;
};

export const initialState = {
  threads: [],
  viewingStatus: COMMENT_VIEWING_STATUS.UNRESOLVED,
  isLoading: false,
  hasLoaded: false,
  hasError: false,
  addCommentError: false,
  addCommentLoading: 'idle',
  hasCommented: false,
  errorStatusCode: null,
  projectId: undefined,
  entityType: '',
  entityId: undefined,
  commentPanelRendered: false,
  showCommentsPanel: false,
  commentPanelOpened: false,
  entityVersion: -1,
  versionRequired: undefined,
  isEntitySharedWithClient: false,
  embedEditor: true,
  hasCommentSelection: false,
  commentCounts: null,
  commentCountsFulfilled: false,
  isCommentCountsValid: false,
  resolveAllError: null,
  pathname: '',
  openNewCommentThread: false,
  activeThreadId: '',
  resolvedThreadId: '',
  openNewInlineCommentThread: null,
  ribbonPanelItem: { type: null },
};

const commentsSlice = createSlice({
  name: 'comments',
  initialState,
  reducers: {
    setEntityId: (state, action) => ({
      ...state,
      entityId: action.payload,
    }),
    resetComments: (state) => ({
      ...initialState,
      commentPanelRendered: state.commentPanelRendered,
    }),
    fetchCommentsBegan: (state) => ({
      ...state,
      isLoading: true,
      hasLoaded: false,
      hasError: false,
      errorStatusCode: null,
    }),
    fetchCommentsSucceeded: (state, action) => {
      const { entityType, entityId } =
        action.payload.length && action.payload[0];
      const isForSameThread =
        entityType === state.entityType && entityId === +state.entityId;
      const threadWithoutComments = action.payload.length === 0;
      const loadCommentThreads = isForSameThread || threadWithoutComments;
      return {
        ...state,
        threads: loadCommentThreads ? action.payload : [],
        hasLoaded: true,
        hasError: false,
        errorStatusCode: null,
        openNewCommentThread: false,
      };
    },
    fetchCommentsFailed: (state, action) => ({
      ...state,
      isLoading: false,
      hasLoaded: false,
      hasError: true,
      errorStatusCode: action.payload,
    }),
    setHasCommented: (state, action) => ({
      ...state,
      hasCommented: true,
      activeThreadId: action.payload,
    }),
    viewUnresolvedComments: (state) => ({
      ...state,
      viewingStatus: COMMENT_VIEWING_STATUS.UNRESOLVED,
    }),
    viewResolvedComments: (state) => ({
      ...state,
      viewingStatus: COMMENT_VIEWING_STATUS.RESOLVED,
    }),
    initializeCommentsPanel: (_state, action) => {
      const {
        projectId,
        entityType,
        entityId,
        versionRequired,
        embedEditor,
        showCommentsPanel = true,
        pathname,
      } = action.payload;
      return {
        ...initialState,
        commentPanelRendered: _state.commentPanelRendered,
        ribbonPanelItem:
          _state.ribbonPanelItem.type === 'versioning'
            ? _state.ribbonPanelItem
            : { type: null },
        commentPanelOpened:
          _state.ribbonPanelItem.type === 'versioning'
            ? _state.commentPanelOpened
            : false,
        projectId,
        entityType,
        entityId,
        versionRequired,
        embedEditor: embedEditor === undefined,
        showCommentsPanel,
        pathname,
      };
    },
    setCommentPanelRendered: (state, action) => ({
      ...state,
      commentPanelRendered: action.payload,
    }),
    setCommentPanelOpened: (state, action) => ({
      ...state,
      commentPanelOpened: action.payload,
    }),
    entitySharedStatusUpdateSuccess: (state, action) => ({
      ...state,
      isEntitySharedWithClient: action.payload,
    }),
    setHasCommentSelection: (state, action) => ({
      ...state,
      hasCommentSelection: action.payload,
    }),
    setIsLoading: (state, action) => ({
      ...state,
      isLoading: action.payload,
    }),
    resolveAllSuccess: (state) => ({
      ...state,
      resolveAllError: null,
    }),
    resolveAllFailed: (state, action) => ({
      ...state,
      resolveAllError: action.payload,
    }),
    setOpenNewCommentThread: (state, action) => ({
      ...state,
      openNewCommentThread: action.payload,
    }),
    setActiveThreadId: (state, action) => ({
      ...state,
      activeThreadId: action.payload,
    }),
    setCommentCountsValid: (state, _action) => ({
      ...state,
      isCommentCountsValid: !state.isCommentCountsValid,
    }),
    setResolvedThreadId: (state, action) => ({
      ...state,
      resolvedThreadId: action.payload,
    }),
    setOpenNewInlineCommentThread: (state, action) => ({
      ...state,
      openNewInlineCommentThread: action.payload,
    }),
    setRibbonPanelItem: (state, { payload }) => ({
      ...state,
      ribbonPanelItem: payload,
    }),
  },
  extraReducers: (builder) => {
    builder.addCase(updateEntityVersion, (state, action) => ({
      ...state,
      entityVersion: action.payload,
    }));
    builder.addCase(addCommentToEntity.pending, (state, action) => {
      state.hasCommented = true;
      state.activeThreadId = action.meta.arg.threadId;
      state.addCommentLoading = 'pending';
      state.addCommentError = false;
    });
    builder.addCase(addCommentToEntity.fulfilled, (state) => {
      state.hasCommentSelection = false;
      state.isCommentCountsValid = !state.isCommentCountsValid;
      state.addCommentLoading = 'idle';
      state.addCommentError = false;
    });
    builder.addCase(addCommentToEntity.rejected, (state) => {
      state.addCommentLoading = 'idle';
      state.addCommentError = true;
    });
    builder.addCase(getCommentCounts.pending, (state) => {
      state.commentCountsFulfilled = false;
    });
    builder.addCase(getCommentCounts.fulfilled, (state, action) => {
      state.commentCounts = action.payload;
      state.commentCountsFulfilled = true;
    });
    builder.addCase(getCommentCounts.rejected, (state) => {
      state.commentCountsFulfilled = false;
    });
    builder.addCase(getCommentCountsByContentId.pending, (state) => {
      state.commentCountsFulfilled = false;
    });
    builder.addCase(getCommentCountsByContentId.fulfilled, (state, action) => {
      state.commentCounts = action.payload;
      state.commentCountsFulfilled = true;
    });
    builder.addCase(getCommentCountsByContentId.rejected, (state) => {
      state.commentCountsFulfilled = false;
    });
    builder.addCase(deleteCommentThread.fulfilled, (state, _action) => {
      state.isCommentCountsValid = !state.isCommentCountsValid;
    });
  },
});
export default commentsSlice.reducer;

// Actions
export const {
  setEntityId,
  resetComments,
  fetchCommentsBegan,
  fetchCommentsSucceeded,
  fetchCommentsFailed,
  setHasCommented,
  viewUnresolvedComments,
  viewResolvedComments,
  initializeCommentsPanel,
  setCommentPanelRendered,
  setCommentPanelOpened,
  setOpenNewInlineCommentThread,
  updateCommentPanel,
  entitySharedStatusUpdateSuccess,
  setHasCommentSelection,
  setIsLoading,
  resolveAllSuccess,
  resolveAllFailed,
  setOpenNewCommentThread,
  setActiveThreadId,
  setCommentCountsValid,
  setResolvedThreadId,
  setRibbonPanelItem,
} = commentsSlice.actions;

// Selectors
export const getComments = (state) => state.comments;
export const getCommentsHasLoaded = (state) => state.comments.hasLoaded;
export const getCommentsIsLoading = (state) => state.comments.isLoading;
export const getHasCommented = (state) => state.comments.hasCommented;
export const getViewingStatus = (state) => state.comments.viewingStatus;
export const getCurrentCommentThreads = (state) => state.comments.threads || [];
export const getCommentPanelRendered = (state) =>
  state.comments.commentPanelRendered;
export const getCommentPanelOpened = (state) =>
  state.comments.commentPanelOpened;
export const getCurrentComments = (state) =>
  _.map(state.comments.threads, 'comments').flat();
export const getHasCommentSelection = (state) =>
  state.comments.hasCommentSelection;
export const getCommentCountsByVersions = (state) =>
  state.comments.commentCounts;
export const getCommentCountsFulfilled = (state) =>
  state.comments.commentCountsFulfilled;
export const isCommentCountValid = (state) =>
  state.comments.isCommentCountsValid;
export const getEntityType = (state) => state.comments.entityType;
export const getOpenNewCommentThread = (state) =>
  state.comments.openNewCommentThread;
export const getActiveThreadId = (state) => state.comments.activeThreadId;
export const getEntityVersion = (state) => state.comments.entityVersion;
export const getResolvedThreadId = (state) => state.comments.resolvedThreadId;
export const getEntityId = (state) => state.comments.entityId;
export const getOpenNewInlineCommentThread = (state) =>
  state.comments.openNewInlineCommentThread;
export const getRibbonPanelItem = (state) => state.comments.ribbonPanelItem;

// Thunks
export const loadCommentsThunk =
  (
    projectId,
    entityType,
    entityId,
    entityVersion,
    isEntitySharedWithClient = false
  ) =>
  async (dispatch, getState) => {
    try {
      const state = getState();
      const { comments } = state;
      const { isLoading } = comments;

      if (!isLoading) {
        dispatch(fetchCommentsBegan());

        const response = await getCommentsByEntity(
          projectId,
          entityType,
          entityId,
          entityVersion,
          isEntitySharedWithClient
        );

        dispatch(fetchCommentsSucceeded(response));
      }
    } catch (error) {
      dispatch(
        fetchCommentsFailed(error.response ? error.response.status : 500)
      );
    }
  };

export const updateCommentThunk = (comment) => async (dispatch, getState) => {
  try {
    const state = getState();
    const existingComment = getCurrentComments(state).find(
      (currentComment) =>
        currentComment.commentId === comment.commentId ||
        currentComment.commentId === comment.id
    );

    const oldCopy = { ...existingComment };
    const updatedComment = updateValues(existingComment, comment);

    if (!_.isEqual(oldCopy, updatedComment)) {
      await updateComment(updatedComment);
    }

    const { projectId, entityType, entityId, entityVersion } = state.comments;

    dispatch(loadCommentsThunk(projectId, entityType, entityId, entityVersion));
  } catch (error) {
    dispatch(fetchCommentsFailed(error.response ? error.response.status : 500));
  }
};

export const toggleResolveCommentThreadThunk =
  (comment, currentEntityVersion) => async (dispatch, getState) => {
    try {
      const state = getState();
      const { comments } = state;
      const { isEntitySharedWithClient } = comments;

      await resolveCommentThread(comment);

      const { projectId, entityType, entityId } = comment;

      dispatch(fetchCommentsBegan());
      const response = await getCommentsByEntity(
        projectId,
        entityType,
        entityId,
        currentEntityVersion,
        isEntitySharedWithClient
      );
      dispatch(setCommentCountsValid());
      dispatch(fetchCommentsSucceeded(response));
    } catch (error) {
      dispatch(
        fetchCommentsFailed(error.response ? error.response.status : 500)
      );
    }
  };

export const resolveAllCommentsThunk = () => async (dispatch, getState) => {
  const state = getState();
  const { comments } = state;
  const {
    projectId,
    entityType,
    entityId,
    entityVersion,
    isEntitySharedWithClient,
    threads,
  } = comments;

  try {
    const threadIds = threads
      .filter((thread) => !thread.resolved)
      .map((thread) => thread.id);

    if (threadIds.length === 0) {
      return;
    }

    await resolveAllComments(projectId, entityType, entityId, threadIds);

    dispatch(resolveAllSuccess());

    setTimeout(() => {
      dispatch(
        loadCommentsThunk(
          projectId,
          entityType,
          entityId,
          entityVersion,
          isEntitySharedWithClient
        )
      );

      dispatch(setCommentCountsValid());
    }, 25);
  } catch (error) {
    const { response } = error;
    const { status } = response;

    dispatch(resolveAllFailed(status));
  }
};
